import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "@/i18n";

const MaskedLine = ({ children, delay, className }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  </div>
);

const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 240]);
  const gearY = useTransform(scrollYProgress, [0, 1], [0, -120]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex flex-col justify-center overflow-hidden border-b border-white/10" data-testid="hero-section">
      <motion.div
        style={{ y: glowY }}
        className="absolute -top-1/4 -right-1/4 w-[80vw] h-[80vw] rounded-full opacity-25 blur-3xl pointer-events-none"
      >
        <div className="w-full h-full rounded-full" style={{ background: "radial-gradient(circle, rgba(255,90,0,0.55) 0%, rgba(255,90,0,0.08) 45%, transparent 70%)" }} />
      </motion.div>

      <motion.img
        src="/assets/paq-gear.webp"
        alt=""
        style={{ y: gearY }}
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, duration: 90, ease: "linear" }}
        className="absolute right-[-8vw] md:right-[-4vw] top-1/2 -translate-y-1/2 w-[46vw] max-w-[640px] opacity-[0.13] pointer-events-none select-none"
      />

      <div className="relative z-10 px-5 md:px-10 pt-28 pb-16 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="font-mono text-[10px] md:text-xs tracking-[0.35em] text-thermal mb-8 md:mb-12"
          data-testid="hero-eyebrow"
        >
          {t.hero.eyebrow}
        </motion.p>

        <h1 className="font-display font-semibold leading-[0.92] tracking-tight text-[15vw] md:text-[11.5vw]" data-testid="hero-title">
          <MaskedLine delay={0.15}>{t.hero.line1}</MaskedLine>
          <MaskedLine delay={0.3} className="text-outline">{t.hero.line2}</MaskedLine>
          <MaskedLine delay={0.45} className="text-thermal">{t.hero.line3}</MaskedLine>
        </h1>

        <div className="mt-10 md:mt-16 flex flex-col md:flex-row md:items-end justify-between gap-10">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-xl text-sm md:text-base leading-relaxed text-white/70"
            data-testid="hero-subtitle"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.4 }}
            className="font-mono text-[10px] md:text-xs tracking-widest text-white/60 space-y-2 md:text-right"
            data-testid="hero-status"
          >
            <div className="flex md:justify-end items-center gap-2">
              <span className="w-2 h-2 bg-thermal blink-dot" />
              <span>{t.hero.status}</span>
            </div>
            <div className="flex md:justify-end items-center gap-2">
              <span className="w-2 h-2 bg-micro blink-dot" style={{ animationDelay: "0.5s" }} />
              <span>{t.hero.cycle}</span>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 left-5 md:left-10 font-mono text-[10px] tracking-[0.4em] text-white/40 flex items-center gap-3"
        data-testid="hero-scroll-hint"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>↓</motion.span>
        {t.hero.scroll}
      </motion.div>
    </section>
  );
};

export default Hero;
